import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdArrowBack, MdCalendarToday, MdPerson, MdLocalHospital,
  MdImage, MdPictureAsPdf, MdOpenInNew, MdMedication, MdContentCopy,
} from 'react-icons/md';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import usePoll from '../hooks/usePoll';

function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(d);
  }
}

function fileKind(file) {
  const url = (file?.url || '').toLowerCase();
  const type = (file?.type || '').toLowerCase();
  if (type === 'pdf' || url.endsWith('.pdf')) return 'pdf';
  if (type === 'image' || /\.(jpe?g|png|webp|gif)$/.test(url)) return 'image';
  return 'other';
}

function FileTile({ file, onImageClick }) {
  const url = file.url || '';
  const name = file.name || 'Prescription';
  const kind = fileKind(file);

  if (kind === 'image' && url) {
    return (
      <button type="button" className="rx-file-tile rx-file-image" onClick={() => onImageClick(url)}>
        <img src={url} alt={name} loading="lazy" />
        <div className="rx-file-overlay">
          <MdImage size={14} />
          <span>{name}</span>
        </div>
      </button>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="rx-file-tile rx-file-pdf">
      <MdPictureAsPdf size={42} />
      <div className="rx-file-pdf-name">{name}</div>
      <div className="rx-file-pdf-open"><MdOpenInNew size={12} /> Open PDF</div>
    </a>
  );
}

function PrescriptionCard({ rx, onImageClick, onCopyId }) {
  const bookingRef = rx.bookingId || rx.bookingIds || '';
  const meds = Array.isArray(rx.medications) ? rx.medications : [];
  const files = Array.isArray(rx.files) ? rx.files : [];
  const hasTyped = meds.length > 0 || rx.diagnosis;
  const tag = rx.diagnosis
    ? rx.diagnosis
    : files.length > 0
      ? `${files.length} file${files.length > 1 ? 's' : ''}`
      : hasTyped ? 'Typed Rx' : 'Prescription';

  return (
    <div className="rx-card">
      <div className="rx-card-header">
        <div className="rx-card-icon"><MdMedication size={22} /></div>
        <div className="rx-card-meta">
          <div className="rx-card-doctor">Dr. {rx.doctorName || rx.doctor || 'Doctor'}</div>
          <div className="rx-card-sub">
            <span><MdLocalHospital size={12} /> {rx.hospitalName || rx.hospital || 'Hospital'}</span>
            <span><MdCalendarToday size={12} /> {fmtDate(rx.createdAt || rx.date)}</span>
            {rx.patientName && <span><MdPerson size={12} /> {rx.patientName}</span>}
          </div>
        </div>
        <span className="rx-card-tag">{tag}</span>
      </div>

      {bookingRef && (
        <button
          type="button"
          className="rx-booking-ref"
          onClick={() => onCopyId(bookingRef)}
          title="Tap to copy Booking ID"
        >
          <span className="rx-booking-ref-label">BOOKING ID</span>
          <span className="rx-booking-ref-id">{bookingRef}</span>
          <span className="rx-booking-ref-hint"><MdContentCopy size={12} /> Tap to copy</span>
        </button>
      )}

      {rx.diagnosis && (
        <div className="rx-diagnosis">
          <span className="rx-diagnosis-label">Diagnosis</span>
          <span className="rx-diagnosis-value">{rx.diagnosis}</span>
        </div>
      )}

      {rx.notes && (
        <div className="rx-notes">📝 {rx.notes}</div>
      )}

      {files.length > 0 && (
        <div className="rx-section">
          <div className="rx-section-title">Prescription images / PDF</div>
          <div className="rx-file-grid">
            {files.map((f, j) => (
              <FileTile key={j} file={f} onImageClick={onImageClick} />
            ))}
          </div>
        </div>
      )}

      {meds.length > 0 && (
        <div className="rx-section">
          <div className="rx-section-title">Medications</div>
          <div className="rx-med-list">
            {meds.map((med, j) => (
              <div key={j} className="rx-med-item">
                <div className="rx-med-top">
                  <strong>{med.name || med.medicine}</strong>
                  {med.duration && <span className="rx-med-duration">{med.duration}</span>}
                </div>
                <div className="rx-med-details">
                  {med.dosage && <span>💊 {med.dosage}</span>}
                  {med.frequency && <span>⏰ {med.frequency}</span>}
                  {med.timing && <span>🍽️ {med.timing}</span>}
                </div>
                {med.instructions && (
                  <div className="rx-med-instructions">⚠️ {med.instructions}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!files.length && !meds.length && !rx.notes && !rx.diagnosis && (
        <div className="rx-empty-detail">No prescription details available</div>
      )}
    </div>
  );
}

export default function PrescriptionsPage() {
  const navigate = useNavigate();
  const { user, showToast } = useAuth();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewImage, setViewImage] = useState(null);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const r = await api.getMyPrescriptionsAll(user);
    setPrescriptions(r.data || []);
    setLoading(false);
  }, [user]);

  usePoll(load, 5000);

  if (!user) {
    return (
      <div className="page empty">
        <div className="empty-icon">🔒</div>
        <div className="empty-title">Login to view prescriptions</div>
        <button type="button" className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/login')}>
          Login / Sign Up
        </button>
      </div>
    );
  }

  return (
    <div className="fade-in rx-page">
      <div className="rx-page-header">
        <button type="button" onClick={() => navigate(-1)} className="btn btn-ghost btn-sm" style={{ padding: 8 }}>
          <MdArrowBack size={20} />
        </button>
        <div>
          <div className="rx-page-title">My Prescriptions</div>
          <div className="rx-page-sub">{prescriptions.length} prescription(s)</div>
        </div>
      </div>

      <div className="page">
        {loading ? (
          <div className="skeleton-list">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton-card" style={{ height: 160, marginBottom: 12, borderRadius: 16 }} />
            ))}
          </div>
        ) : prescriptions.length === 0 ? (
          <div className="empty" style={{ paddingTop: 60 }}>
            <div className="empty-icon">💊</div>
            <div className="empty-title">No prescriptions yet</div>
            <div className="empty-sub">
              After your consultation, your doctor&apos;s prescriptions will appear here automatically.
            </div>
          </div>
        ) : (
          <div className="rx-card-list">
            {prescriptions.map((rx, i) => (
              <PrescriptionCard
                key={rx.prescriptionId || i}
                rx={rx}
                onImageClick={setViewImage}
                onCopyId={(id) => {
                  navigator.clipboard?.writeText(id)
                    .then(() => showToast('Booking ID copied', 'success'))
                    .catch(() => showToast('Could not copy', 'error'));
                }}
              />
            ))}
          </div>
        )}
      </div>

      {viewImage && (
        <div className="rx-lightbox" onClick={() => setViewImage(null)} role="presentation">
          <img src={viewImage} alt="Prescription" onClick={e => e.stopPropagation()} />
          <button type="button" className="rx-lightbox-close" onClick={() => setViewImage(null)}>✕</button>
        </div>
      )}
    </div>
  );
}
